/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The seeded image per CMS slot — what a page shows until an admin uploads
 * something for that slot. One definition, shared the staticRoutes.js way
 * (plain ESM, zero imports) by:
 *   1. src/hooks/useImageMappings.ts — the fallback behind every getImage();
 *   2. server/config.ts — the fallback behind /api/image-mappings and the
 *      seed of image_mappings.json on a fresh environment;
 *   3. server/lcpPreload.ts — which file the hero preload names for a route.
 *
 * Until 2026-10-01 the server kept its own copy with five of the pages, so
 * a route whose slot was only known to the client could not be preloaded
 * (and a fresh environment's /api/image-mappings disagreed with the page).
 */

/** @type {Readonly<Record<string, Readonly<Record<string, string>>>>} */
export const DEFAULT_IMAGE_MAPPINGS = Object.freeze({
  hero: {
    hero_bg: '/images/hero-new.webp',
    info_bg: '/images/about-sustainability.webp',
  },
  how_it_works: { main: '/images/how-it-works.webp' },
  about: {
    hero_bg: '/images/about-renewable.webp',
    mission: '/images/about-data.webp',
    how_we_compare: '/images/comparison_analysis.webp',
  },
  how_we_compare: {
    hero_bg: '/images/ere-concept.webp',
    independence: '/images/trust_handshake.webp',
  },
  contact: {
    hero_bg: '/images/customer-service.webp',
    main: '/images/contact.webp',
  },
  faq: {
    background: '/images/about-veru.webp',
    pricing: '/images/about-profitable.webp',
  },
  partner: { hero_bg: '/images/service-partnership.webp' },
  // Wind turbines over a Dutch polder: no product, car or brand in frame and
  // no figures. Was ere-marketplace.webp until 2026-09-23 (a generated trading
  // screen with dollar prices behind a page about a euro market), then
  // charger-render-a.webp until 2026-10-14 — a render that copies Alfen's
  // wallbox design, which ASSET-PROVENANCE.md says must not be shown. A seeded
  // slot never names a file the register marks "Niet publiceren"
  // (src/lib/__tests__/imageDefaults.test.ts).
  nea: { hero_bg: '/images/wind-turbines.webp' },
  geen_laadpaal: { hero_bg: '/images/ev_charging_home.webp' },
  privacy: { hero_bg: '/images/secure-payout.webp' },
  terms: { hero_bg: '/images/business-analysis.webp' },
  cookies: { hero_bg: '/images/consulting.webp' },
  // A Dutch home with a wallbox: no figures, prices or brand names on it —
  // a page about what the site's information does not promise shows none.
  disclaimer: { hero_bg: '/images/sustainable_home.webp' },
  mid_check: {
    hero_bg: '/images/smart-charger-mid.webp',
    context_what: '/images/mid_meter_closeup.webp',
    context_deadline: '/images/smart-meter.webp',
    context_smart: '/images/ev_charging_home.webp',
  },
  // The laadpalen brand hub (ChargerBrandsPage) opens on the MID check's hero:
  // the same subject — which charge point has a MID meter — and a file every
  // environment already ships. The brand pages have no hero.
  charger_brands: { hero_bg: '/images/smart-charger-mid.webp' },
});
